import { computed, inject, Injectable, OnDestroy, signal } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";

import { lastValueFrom, Subject } from "rxjs";

import { BulkAddProductsModel } from "@components/products/bulk-add-products/bulk-add-products.component.model";
import { ConfirmDialogComponent } from "@components/shared";
import { BulkAddBaseService } from "@components/shared/bulk-add/bulk-add-base.service";
import { TextConstants } from "@shared/constants";
import {
  ConfirmDialogResponseEnum,
  CustomFieldsResourceTypeEnum,
  RecordStateEnum,
} from "@shared/enums";
import { IMaterial, ISelectOption, ITagExtended, IUserData } from "@shared/interfaces";
import {
  NotificationService,
  AuthenticationService,
  CustomFieldsService,
  MaterialsService,
  CommonService,
} from "@shared/services";
import { RouterService } from "@shared/services/router.service";
import { FormUtils, LocalizationUtils } from "@shared/utils";
import { CustomValidators } from "@shared/validators";

@Injectable({
  providedIn: "root",
})
export class BulkAddProductsService extends BulkAddBaseService implements OnDestroy {
  private readonly dialog = inject(MatDialog);

  private readonly authenticationService = inject(AuthenticationService);

  private readonly materialsService = inject(MaterialsService);

  private readonly commonService = inject(CommonService);

  override customFieldsService = inject(CustomFieldsService);

  private readonly notificationService = inject(NotificationService);

  private readonly routerService = inject(RouterService);

  private readonly fb = inject(FormBuilder);

  readonly isRegularUser = this.authenticationService.isRegularUser();

  readonly refreshTableSubject = new Subject();

  abortController: AbortController;

  initialFormValue: object;

  override formGroup: FormGroup<BulkAddProductsModel.SetValuesFormGroup>;

  public readonly unitOfMeasurementOptions = computed(() =>
    this.commonService.systemUnitsOfMeasurements()?.map((unit) => ({
      label: LocalizationUtils.getUnitOfMeasurementCategory(unit.type),
      value: unit.id,
    })),
  );

  materialOptions = signal<ISelectOption[]>([]);

  allMaterials: IMaterial[] = [];

  private readonly fieldEnum = BulkAddProductsModel.FieldEnum;

  constructor() {
    super();

    this.subscriptions.add(
      this.authenticationService.userDataObservable$.subscribe((userData: IUserData) => {
        if (!userData || !this.isRegularUser) {
          return;
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.materialOptions.set([]);
    this.allCustomFields.set([]);
  }

  async initializeProperties(): Promise<void> {
    this.abortController = new AbortController();

    await Promise.all([this.setMaterials(), this.setAllCustomFields()]);
  }

  setupForm(): void {
    const { fieldEnum } = this;

    const customFieldsFormArray = this.createCustomFieldsFormArray(this.allCustomFields());

    const formControls = {
      [fieldEnum.TAGS]: this.fb.control<ITagExtended[]>([]),
      [fieldEnum.BASE_UNIT]: this.fb.control<ISelectOption>(null),
      [fieldEnum.ALLOWED_MATERIALS]: this.fb.control<ISelectOption[]>([]),
      [fieldEnum.HS_CODE]: this.fb.control<string>(null, [
        CustomValidators.digitsAndDots,
        CustomValidators.maxLength(50),
      ]),
      [fieldEnum.DESCRIPTION]: this.fb.control<string>(null, [CustomValidators.maxLength(255)]),
      [fieldEnum.IS_FIXED_TAGS]: this.fb.control(false),
      [fieldEnum.IS_FIXED_BASE_UNIT]: this.fb.control(false),
      [fieldEnum.IS_FIXED_ALLOWED_MATERIALS]: this.fb.control(false),
      [fieldEnum.IS_FIXED_HS_CODE]: this.fb.control(false),
      [fieldEnum.IS_FIXED_DESCRIPTION]: this.fb.control(false),
      [fieldEnum.CUSTOM_FIELDS]: customFieldsFormArray,
      [fieldEnum.RECORDS]: this.fb.array<FormGroup<BulkAddProductsModel.ProductFormGroup>>([]),
    };

    this.formGroup = new FormGroup<BulkAddProductsModel.SetValuesFormGroup>(formControls);

    this.initialFormValue = this.formGroup.getRawValue();

    this.subscribeToRecordsLengthChanges();
  }

  async openSlideOver() {
    await this.routerService.navigate(this.routerService.getBulkAddProductsLink());
  }

  get refreshTable$() {
    return this.refreshTableSubject.asObservable();
  }

  async setAllCustomFields() {
    const customFields = await this.customFieldsService.getAll(
      CustomFieldsResourceTypeEnum.PRODUCT,
      RecordStateEnum.ACTIVE,
    );

    this.allCustomFields.set(customFields);
  }

  abortCreation() {
    this.abortController.abort();
  }

  hasInitialFormValueChanged(currentFormValue: object) {
    return FormUtils.hasInitialFormValueChanged(this.initialFormValue, currentFormValue);
  }

  async canExitSlideOver() {
    const hasChanged =
      !this.formGroup.pristine && this.hasInitialFormValueChanged(this.formGroup.getRawValue());

    if (!hasChanged) {
      return true;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: TextConstants.EXIT_CONFIRMATION,
        contentText: $localize`:@@confirm.discardchanges:You're about to leave this page. Are you sure you want to discard your changes?`,
        confirmButtonText: TextConstants.DISCARD_CHANGES_LEAVE,
        confirmButtonColor: "danger",
        confirmButtonIcon: "logout",
      },
    });

    const response = await lastValueFrom(dialogRef.afterClosed());

    return response === ConfirmDialogResponseEnum.CONFIRM;
  }

  async setMaterials() {
    try {
      this.allMaterials = await this.materialsService.getAll(RecordStateEnum.ACTIVE);

      const materialOptions: ISelectOption[] = this.allMaterials
        .map((material) => ({
          label: material.name,
          value: material.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

      this.materialOptions.set(materialOptions);
    } catch (error) {
      this.notificationService.showError(error);
    }
  }
}
